import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { submitRegret } from '../api';
import { CategoryTag, CATEGORY_LABELS, CATEGORY_COLORS } from '../types';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const MAX_CHARS = 180;

// Keyword-based category detection
const CATEGORY_KEYWORDS: Record<CategoryTag, string[]> = {
  love: ['love', 'loved', 'heart', 'relationship', 'boyfriend', 'girlfriend', 'husband', 'wife', 'partner', 'marriage', 'divorce', 'crush', 'kiss', 'breakup', 'cheated', 'affair', 'soulmate', 'ex'],
  career: ['job', 'career', 'work', 'boss', 'promotion', 'fired', 'quit', 'business', 'company', 'degree', 'college', 'university', 'school', 'opportunity', 'dream job', 'salary'],
  family: ['family', 'mom', 'mother', 'dad', 'father', 'parent', 'sibling', 'brother', 'sister', 'son', 'daughter', 'child', 'children', 'kids', 'grandma', 'grandpa', 'aunt', 'uncle'],
  money: ['money', 'debt', 'loan', 'savings', 'invest', 'stock', 'crypto', 'bitcoin', 'spent', 'bought', 'expensive', 'rich', 'poor', 'broke', 'bankrupt', 'gambling'],
  health: ['health', 'sick', 'illness', 'doctor', 'hospital', 'medicine', 'smoke', 'smoking', 'drink', 'drinking', 'alcohol', 'addiction', 'exercise', 'gym', 'weight', 'diet', 'mental', 'anxiety', 'depression'],
  other: [],
};

function detectCategory(text: string): CategoryTag {
  const lowerText = text.toLowerCase();
  const scores: Record<CategoryTag, number> = { love: 0, career: 0, family: 0, money: 0, health: 0, other: 0 };

  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    for (const keyword of keywords) {
      if (lowerText.includes(keyword)) {
        scores[category as CategoryTag]++;
      }
    }
  }

  let maxCategory: CategoryTag = 'other';
  let maxScore = 0;
  for (const [category, score] of Object.entries(scores)) {
    if (score > maxScore) {
      maxScore = score;
      maxCategory = category as CategoryTag;
    }
  }

  return maxScore > 0 ? maxCategory : 'other';
}

function PaymentForm({ regretId }: { regretId: string }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    const { error: paymentError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/r/${regretId}`,
      },
      redirect: 'if_required',
    });

    if (paymentError) {
      setError(paymentError.message || 'Payment failed');
      setIsProcessing(false);
    } else {
      // Payment succeeded, navigate to the regret page
      navigate(`/r/${regretId}?success=true`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <PaymentElement />
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="btn btn-primary w-full text-lg py-4"
      >
        {isProcessing ? 'Processing...' : 'Pay $1 and Post Forever'}
      </button>
    </form>
  );
}

export default function Submit() {
  const [text, setText] = useState('');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [regretId, setRegretId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charCount = text.length;
  const charsRemaining = MAX_CHARS - charCount;
  const detectedCategory = useMemo(() => detectCategory(text), [text]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    if (newText.length <= MAX_CHARS) {
      setText(newText);
    }
  };

  const handleProceedToPayment = async () => {
    if (text.trim().length === 0) {
      setError('Please write your regret first');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await submitRegret(text.trim());
      setClientSecret(response.clientSecret);
      setRegretId(response.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If we have a client secret, show the payment form
  if (clientSecret && regretId) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif font-semibold text-gray-900 mb-2">
            Almost there
          </h1>
          <p className="text-gray-600">
            Complete your payment to post your regret forever.
          </p>
        </div>

        {/* Preview of the regret */}
        <div className={`${CATEGORY_COLORS[detectedCategory]} rounded-xl p-6 mb-6`}>
          <p className="font-serif text-gray-800 text-lg leading-relaxed">
            "{text}"
          </p>
          <div className="mt-4 text-sm text-gray-600">
            Category: {CATEGORY_LABELS[detectedCategory]}
          </div>
        </div>

        {/* Permanence warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <span className="text-amber-600 text-lg">⚠️</span>
            <div className="text-sm">
              <p className="font-semibold text-amber-900">This is permanent</p>
              <p className="text-amber-800 mt-1">
                Once you pay, this regret will be on the wall forever.
                No edits. No deletes. No exceptions. Read it one more time.
              </p>
            </div>
          </div>
        </div>

        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#1f2937',
                borderRadius: '8px',
              },
            },
          }}
        >
          <PaymentForm regretId={regretId} />
        </Elements>

        <button
          onClick={() => {
            setClientSecret(null);
            setRegretId(null);
          }}
          className="w-full text-center text-gray-500 hover:text-gray-700 mt-4 text-sm"
        >
          Go back and edit
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-4 py-12">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-serif font-semibold text-gray-900 mb-2">
          Share your regret
        </h1>
        <p className="text-gray-600">
          No name. No email. No account needed. Just $1.
        </p>
      </div>

      {/* Text input */}
      <div className="relative mb-6">
        <textarea
          value={text}
          onChange={handleTextChange}
          placeholder="I regret..."
          rows={6}
          className={`
            w-full px-6 py-5 text-xl font-serif leading-relaxed
            ${CATEGORY_COLORS[detectedCategory]} rounded-xl
            border-2 border-transparent focus:border-gray-400
            placeholder-gray-400 resize-none outline-none
            transition-colors duration-200
          `}
        />

        {/* Character counter */}
        <div className={`
          absolute bottom-4 right-4 text-sm font-medium
          ${charsRemaining < 20 ? 'text-red-500' : 'text-gray-400'}
        `}>
          {charsRemaining}
        </div>
      </div>

      {/* Category suggestion */}
      {text.length > 10 && (
        <div className="mb-6 flex items-center gap-2">
          <span className="text-sm text-gray-500">Detected category:</span>
          <span className={`
            px-3 py-1 rounded-full text-sm font-medium
            ${CATEGORY_COLORS[detectedCategory]} border border-black/10
          `}>
            {CATEGORY_LABELS[detectedCategory]}
          </span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Info boxes */}
      <div className="space-y-4 mb-6">
        {/* Permanence warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <span className="text-amber-600 text-lg">⚠️</span>
            <div className="text-sm">
              <p className="font-semibold text-amber-900">The wall is permanent</p>
              <p className="text-amber-800 mt-1">
                Every regret posted here stays forever. There is no edit button,
                no delete option, no way to take it back. Choose your words carefully.
              </p>
            </div>
          </div>
        </div>

        {/* Anonymous notice */}
        <div className="bg-parchment-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div className="text-sm text-gray-600">
              <p className="font-medium text-gray-800">Completely anonymous</p>
              <p className="mt-1">
                No name, no email, no account. Just a payment confirmation
                that can't be traced back to you.{' '}
                <Link to="/faq" className="text-gray-900 underline hover:no-underline">
                  Learn more
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Submit button */}
      <button
        onClick={handleProceedToPayment}
        disabled={text.trim().length === 0 || isSubmitting}
        className="btn btn-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Processing...' : 'Continue to Payment — $1'}
      </button>

      {/* FAQ link */}
      <p className="text-center text-sm text-gray-500 mt-4">
        Questions?{' '}
        <Link to="/faq" className="text-gray-900 font-medium hover:underline">
          Read our FAQ
        </Link>
      </p>
    </div>
  );
}
